'use client';
import { useEffect, useRef, useState } from 'react';
import { debounce } from 'es-toolkit';

const WISHLIST_WRITE_DEBOUNCE_MS = 1000;

interface UseDebouncedWishlistWriteParams {
  sku: string;
  /** Whether the last fetched wishlist holds this sku. */
  isServerWished: boolean;
  /** False while the wishlist owner is still resolving — `onToggle` drops writes until it settles. */
  isOwnerResolved: boolean;
  /** Applies the add-or-remove. Derives the direction itself from the last fetched wishlist. */
  onToggle: (sku: string) => void;
}

interface UseDebouncedWishlistWriteResult {
  /** What the heart should show: the local guess until the server catches up. */
  isWished: boolean;
  /** Flips the heart immediately and schedules the debounced write. */
  toggle: () => void;
}

/**
 * The optimistic heart shared by the web and native wishlist buttons.
 *
 * Both render a heart that must respond to every tap while the write is rate-limited, and both need
 * the same three guards around that. Keeping one copy is what stops the two surfaces drifting —
 * native previously carried this logic without the owner re-attempt and silently lost a guest's
 * first tap.
 */
export const useDebouncedWishlistWrite = ({
  sku,
  isServerWished,
  isOwnerResolved,
  onToggle,
}: UseDebouncedWishlistWriteParams): UseDebouncedWishlistWriteResult => {
  const [isWished, setIsWished] = useState(isServerWished);

  // The server is the truth; a refetch (or another surface's change) overrides the local guess.
  useEffect(() => {
    setIsWished(isServerWished);
  }, [isServerWished]);

  /**
   * The debounced write is built once and reads its inputs through a ref. Rebuilding it when
   * `onToggle` changes would cancel whatever is pending, and it is a fresh function on every
   * render of the list.
   */
  const latest = useRef({ isWished, isServerWished, isOwnerResolved, onToggle, sku });
  useEffect(() => {
    latest.current = { isWished, isServerWished, isOwnerResolved, onToggle, sku };
  });

  const writeToggle = useRef(
    debounce(() => {
      const current = latest.current;

      // `onToggle` carries no desired state — it derives add-or-remove from the last fetched
      // wishlist. Writing while local and server already agree would apply the very operation the
      // taps cancelled out, so an even number of taps must write nothing at all.
      if (current.isWished === current.isServerWished) {
        return;
      }

      current.onToggle(current.sku);
    }, WISHLIST_WRITE_DEBOUNCE_MS),
  ).current;

  /**
   * `onToggle` returns without mutating until the owner settles, so a tap landing in that window
   * would leave a filled heart the server never heard about — and the sync effect above cannot undo
   * it, since `isServerWished` was false before the tap and stays false after. Re-running the write
   * once the owner resolves re-attempts it; the guard inside decides whether anything is actually
   * outstanding, so a card nobody tapped writes nothing.
   */
  useEffect(() => {
    if (isOwnerResolved && isWished !== isServerWished) {
      writeToggle();
    }
  }, [isOwnerResolved, isWished, isServerWished, writeToggle]);

  /**
   * A card can leave the tree mid-debounce — a route change unmounts it, and the wishlist page
   * drops a card as soon as any write refetches the list. Cancelling would throw the tap away, so
   * the pending write is flushed instead; the guard above still swallows taps that cancelled out,
   * and the write mutates through react-query, which does not need this component to stay mounted.
   */
  useEffect(() => () => writeToggle.flush(), [writeToggle]);

  const toggle = () => {
    setIsWished((wished) => !wished);
    writeToggle();
  };

  return { isWished, toggle };
};
